import { useCallback, useEffect, useMemo, useState } from "react";
import type { EventId, PlanEvent, Task, TaskId } from "../../../../shared/types";
import type { HolidayEntry } from "../../../../shared/hooks/use-holidays";
import type { DayHeaderAction } from "../../types/desktop-calendar-view";
import type { FormAnchor } from "../../hooks/use-week-calendar-state";
import { useDragDrop } from "../../hooks/use-drag-drop";
import { useSlotCreation } from "../../hooks/use-slot-creation";
import { useWeekGridDragHandlers } from "./WeekGridDragHandlers";
import { WeekGridDayHeaders } from "./WeekGridDayHeaders";
import { WeekGridTimeGrid } from "./WeekGridTimeGrid";

interface WeekGridProps {
  readonly days: readonly string[];
  readonly today: string;
  readonly now: Date;
  readonly eventsByDate: ReadonlyMap<string, PlanEvent[]>;
  readonly tasksByDate: ReadonlyMap<string, Task[]>;
  readonly isExpanded?: boolean;
  readonly onEventClick: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onEventLongPress?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onEventContextMenu?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onSlotClick: (
    date: string,
    startTime: string,
    endTime: string,
    anchor?: FormAnchor,
  ) => void;
  readonly onUpdateEvent?: (
    id: EventId,
    updates: Partial<Pick<PlanEvent, "date" | "startTime" | "endTime">>,
  ) => void;
  readonly onToggleExpand?: () => void;
  readonly onCycleTaskStatus?: (id: TaskId) => void;
  readonly onTaskClick?: (task: Task) => void;
  readonly onDayAction?: (day: string, action: DayHeaderAction) => void;
  readonly showAddForm?: boolean;
  readonly onDayContextMenu?: (
    date: string,
    time: string,
    anchorRect: DOMRect,
  ) => void;
  readonly readOnly?: boolean;
  readonly onToggleReadOnly?: () => void;
  readonly selectedDate?: string | null;
  readonly holidays?: ReadonlyMap<string, HolidayEntry>;
}

export function WeekGrid({
  days,
  today,
  now,
  eventsByDate,
  tasksByDate,
  isExpanded,
  onEventClick,
  onEventLongPress,
  onEventContextMenu,
  onSlotClick,
  onUpdateEvent,
  onToggleExpand,
  onCycleTaskStatus,
  onTaskClick,
  onDayAction,
  showAddForm,
  onDayContextMenu,
  readOnly,
  onToggleReadOnly,
  selectedDate,
  holidays,
}: WeekGridProps) {
  const { gridRef, dragging, resizing, ...ddActions } = useDragDrop(
    days,
    onUpdateEvent,
  );
  const slotCreationHook = useSlotCreation();

  useEffect(() => {
    if (!showAddForm) {
      slotCreationHook.cancelSlotCreation();
    }
  }, [showAddForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSlotCreated = useCallback(
    (date: string, startTime: string, endTime: string, anchor: FormAnchor) => {
      onSlotClick(date, startTime, endTime, anchor);
    },
    [onSlotClick],
  );

  const {
    isCursorGrabbing,
    justDraggedRef,
    handleDragStart,
    handleResizeStart,
    handleSlotCreationMouseDown,
  } = useWeekGridDragHandlers(ddActions, slotCreationHook, handleSlotCreated);

  const unlinkedTasksByDate = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const day of days) {
      const dayTasks = tasksByDate.get(day) ?? [];
      const unlinked = dayTasks.filter((t) => !t.eventId && !t.archived);
      if (unlinked.length > 0) {
        map.set(day, unlinked);
      }
    }
    return map;
  }, [days, tasksByDate]);

  const [tasksCollapsed, setTasksCollapsed] = useState(false);
  const hasAnyUnlinkedTasks = Array.from(unlinkedTasksByDate.values()).some((t) => t.length > 0);
  const allUnlinked = useMemo(() => Array.from(unlinkedTasksByDate.values()).flat(), [unlinkedTasksByDate]);
  const totalTasks = allUnlinked.length;
  const doneTasks = allUnlinked.filter((t) => t.status === "done").length;

  return (
    <div
      className={`flex flex-col overflow-hidden flex-1 min-h-0 bg-surface ${isCursorGrabbing ? "cursor-grabbing" : ""}`}
      ref={gridRef}
    >
      <WeekGridDayHeaders
        days={days}
        today={today}
        selectedDate={selectedDate}
        isExpanded={isExpanded}
        readOnly={readOnly}
        onToggleExpand={onToggleExpand}
        onToggleReadOnly={onToggleReadOnly}
        eventsByDate={eventsByDate}
        tasksByDate={tasksByDate}
        onDayAction={readOnly ? undefined : onDayAction}
        hasTasks={hasAnyUnlinkedTasks}
        tasksCollapsed={tasksCollapsed}
        onToggleTasksCollapsed={() => setTasksCollapsed((v) => !v)}
        doneTasks={doneTasks}
        totalTasks={totalTasks}
        holidays={holidays}
      />

      <WeekGridTimeGrid
        days={days}
        today={today}
        now={now}
        eventsByDate={eventsByDate}
        tasksByDate={tasksByDate}
        dragging={readOnly ? null : dragging}
        resizing={readOnly ? null : resizing}
        onEventClick={onEventClick}
        onEventLongPress={readOnly ? undefined : onEventLongPress}
        onEventContextMenu={readOnly ? undefined : onEventContextMenu}
        onSlotClick={readOnly ? undefined : onSlotClick}
        onDragStart={readOnly ? undefined : handleDragStart}
        onResizeStart={readOnly ? undefined : handleResizeStart}
        justDraggedRef={justDraggedRef}
        slotCreation={readOnly ? null : slotCreationHook.slotCreation}
        onSlotCreationMouseDown={readOnly ? undefined : handleSlotCreationMouseDown}
        onDayContextMenu={readOnly ? undefined : onDayContextMenu}
        unlinkedTasksByDate={unlinkedTasksByDate}
        tasksCollapsed={tasksCollapsed}
        onCycleTaskStatus={readOnly ? (() => {}) : (onCycleTaskStatus ?? (() => {}))}
        onTaskClick={onTaskClick}
      />
    </div>
  );
}
